// Messaging Service for Event Platform
import { supabase } from '../lib/supabaseClient';

import cacheService from './browser-cache-service';

class MessagingService {
  // ============================================
  // User Search
  // ============================================

  async searchUsers(searchTerm) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get blocked users to exclude from search
      const { data: blockedUsers } = await supabase
        .from('user_blocks')
        .select('blocked_id')
        .eq('blocker_id', user.id);

      const blockedIds = blockedUsers?.map((b) => b.blocked_id) || [];

      // Search users by display name or email (case-insensitive)
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url, user_type, email')
        .or(`display_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
        .not('id', 'in', `(${[user.id, ...blockedIds].join(',')})`)
        .eq('status', 'active')
        .limit(20);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error searching users:', error);
      return { success: false, error: error.message };
    }
  }

  // ============================================
  // Conversation Management
  // ============================================

  async getConversations() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // First get participant records
      const { data: participants, error: participantError } = await supabase
        .from('conversation_participants')
        .select('conversation_id, unread_count, last_read_at')
        .eq('user_id', user.id)
        .is('left_at', null);

      if (participantError) throw participantError;
      if (!participants || participants.length === 0) {
        return { success: true, data: [] };
      }

      // Get conversation details
      const conversationIds = participants.map((p) => p.conversation_id);
      let conversations;
      let convError;
      try {
        const res = await supabase
          .from('conversations')
          .select('id, last_message_at, last_message_preview, updated_at, metadata')
          .in('id', conversationIds)
          .order('last_message_at', { ascending: false, nullsFirst: false });
        conversations = res.data;
        convError = res.error;
        if (convError) throw convError;
      } catch (err) {
        // Fallback for older schemas where metadata column might not exist
        const res2 = await supabase
          .from('conversations')
          .select('id, last_message_at, last_message_preview, updated_at')
          .in('id', conversationIds)
          .order('last_message_at', { ascending: false, nullsFirst: false });
        conversations = res2.data?.map((c) => ({ ...c, metadata: null })) || [];
      }

      // Merge the data
      const result = conversations.map((conv) => {
        const participant = participants.find((p) => p.conversation_id === conv.id);
        return {
          conversation_id: conv.id,
          unread_count: participant?.unread_count || 0,
          last_read_at: participant?.last_read_at,
          conversations: conv,
        };
      });

      return { success: true, data: result };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return { success: false, error: error.message };
    }
  }

  async getConversationParticipants(conversationId) {
    try {
      // Get participant user IDs
      const { data: participants, error: participantError } = await supabase
        .from('conversation_participants')
        .select('user_id')
        .eq('conversation_id', conversationId)
        .is('left_at', null);

      if (participantError) throw participantError;
      if (!participants || participants.length === 0) {
        return { success: true, data: [] };
      }

      // Get profile information
      const userIds = participants.map((p) => p.user_id).filter((id) => id != null);

      if (userIds.length === 0) {
        return { success: true, data: [] };
      }

      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url, user_type')
        .in('id', userIds);

      if (profileError) throw profileError;

      // Merge the data
      const result = participants.map((p) => ({
        user_id: p.user_id,
        profiles: profiles.find((prof) => prof.id === p.user_id),
      }));

      return { success: true, data: result };
    } catch (error) {
      console.error('Error fetching participants:', error);
      return { success: false, error: error.message };
    }
  }

  async getOrCreateConversation(otherUserId, eventId = null) {
    try {
      const { data, error } = await supabase.rpc('get_or_create_direct_conversation', {
        other_user_id: otherUserId,
      });

      if (error) throw error;

      // If eventId provided, store it in conversation metadata
      if (eventId && data) {
        await supabase
          .from('conversations')
          .update({
            metadata: { eventId },
          })
          .eq('id', data);
      }

      return { success: true, conversationId: data };
    } catch (error) {
      console.error('Error creating conversation:', error);
      return { success: false, error: error.message };
    }
  }

  // ============================================
  // Message Management
  // ============================================

  async getMessages(conversationId, limit = 50, offset = 0) {
    try {
      // Get messages
      const { data: messages, error: messageError } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (messageError) throw messageError;
      if (!messages || messages.length === 0) {
        return { success: true, data: [] };
      }

      // Get unique sender IDs
      const senderIds = [...new Set(messages.map((m) => m.sender_id).filter((id) => id))];

      // Get sender profiles
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url, user_type')
        .in('id', senderIds);

      if (profileError) throw profileError;

      // Get message reactions
      const messageIds = messages.map((m) => m.id);
      const { data: reactions, error: reactionError } = await supabase
        .from('message_reactions')
        .select('id, message_id, user_id, reaction')
        .in('message_id', messageIds);

      if (reactionError) console.warn('Error fetching reactions:', reactionError);

      // Get message status
      const { data: statuses, error: statusError } = await supabase
        .from('message_status')
        .select('message_id, user_id, delivered_at, read_at')
        .in('message_id', messageIds);

      if (statusError) console.warn('Error fetching status:', statusError);

      // Merge all data
      const result = messages.map((msg) => ({
        ...msg,
        profiles: profiles?.find((p) => p.id === msg.sender_id),
        message_reactions: reactions?.filter((r) => r.message_id === msg.id) || [],
        message_status: statuses?.filter((s) => s.message_id === msg.id) || [],
      }));

      // Reverse to show oldest first in UI
      return { success: true, data: result.reverse() };
    } catch (error) {
      console.error('Error fetching messages:', error);
      return { success: false, error: error.message };
    }
  }

  async sendMessage(conversationId, content, attachments = []) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check content moderation
      const { data: isClean } = await supabase.rpc('check_message_content', { content });

      if (!isClean) {
        return {
          success: false,
          error: 'Message contains inappropriate content. Please revise and try again.',
        };
      }

      // Send normal message
      const { data, error } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          content: content.trim(),
          message_type: attachments.length > 0 ? 'file' : 'text',
          attachments: attachments,
        })
        .select()
        .single();

      if (error) throw error;

      // Clear cache
      cacheService.invalidate('messages', `conversation:${conversationId}`);

      return { success: true, data };
    } catch (error) {
      console.error('Error sending message:', error);
      return { success: false, error: error.message };
    }
  }

  async markAsRead(conversationId) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update conversation participant
      await supabase
        .from('conversation_participants')
        .update({
          unread_count: 0,
          last_read_at: new Date().toISOString(),
        })
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id);

      // Update message status
      const { data: unreadMessages } = await supabase
        .from('messages')
        .select('id')
        .eq('conversation_id', conversationId)
        .neq('sender_id', user.id)
        .is('deleted_at', null);

      if (unreadMessages && unreadMessages.length > 0) {
        const now = new Date().toISOString();
        const messageIds = unreadMessages.map((m) => m.id);

        // Check existing statuses for this user
        const { data: existingStatuses, error: statusFetchError } = await supabase
          .from('message_status')
          .select('message_id')
          .in('message_id', messageIds)
          .eq('user_id', user.id);

        if (statusFetchError) {
          console.warn('Status fetch error (non-fatal):', statusFetchError.message);
        }

        const existingIds = new Set((existingStatuses || []).map((s) => s.message_id));
        const toUpdate = messageIds.filter((id) => existingIds.has(id));
        const toInsert = messageIds.filter((id) => !existingIds.has(id));

        if (toInsert.length > 0) {
          const rows = toInsert.map((id) => ({ message_id: id, user_id: user.id, read_at: now }));
          await supabase.from('message_status').insert(rows, { count: 'exact' });
        }

        if (toUpdate.length > 0) {
          await supabase
            .from('message_status')
            .update({ read_at: now })
            .in('message_id', toUpdate)
            .eq('user_id', user.id);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error marking as read:', error);
      return { success: false, error: error.message };
    }
  }

  // ============================================
  // User Management
  // ============================================

  async blockUser(userId) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.from('user_blocks').insert({
        blocker_id: user.id,
        blocked_id: userId,
      });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error blocking user:', error);
      return { success: false, error: error.message };
    }
  }

  async unblockUser(userId) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_blocks')
        .delete()
        .eq('blocker_id', user.id)
        .eq('blocked_id', userId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error unblocking user:', error);
      return { success: false, error: error.message };
    }
  }

  async getBlockedUsers() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get blocked user IDs first
      const { data: blocks, error: blockError } = await supabase
        .from('user_blocks')
        .select('blocked_id, blocked_at')
        .eq('blocker_id', user.id);

      if (blockError) throw blockError;
      if (!blocks || blocks.length === 0) {
        return { success: true, data: [] };
      }

      // Get profile information for blocked users
      const blockedIds = blocks.map((b) => b.blocked_id);
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in('id', blockedIds);

      if (profileError) throw profileError;

      // Merge the data
      const result = blocks.map((block) => ({
        blocked_id: block.blocked_id,
        blocked_at: block.blocked_at,
        profiles: profiles.find((p) => p.id === block.blocked_id),
      }));

      return { success: true, data: result };
    } catch (error) {
      console.error('Error fetching blocked users:', error);
      return { success: false, error: error.message };
    }
  }

  // ============================================
  // Moderation & Reporting
  // ============================================

  async reportMessage(messageId, reason, description = '') {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.from('message_reports').insert({
        message_id: messageId,
        reporter_id: user.id,
        reason,
        description,
      });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error reporting message:', error);
      return { success: false, error: error.message };
    }
  }

  async getPendingMessages() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('pending_messages')
        .select(
          `
          *,
          sender:sender_id (
            id,
            display_name,
            avatar_url
          )
        `
        )
        .eq('recipient_id', user.id)
        .is('approved_at', null)
        .is('rejected_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching pending messages:', error);
      return { success: false, error: error.message };
    }
  }

  async approvePendingMessage(messageId) {
    try {
      const { data: pending, error: fetchError } = await supabase
        .from('pending_messages')
        .select('*')
        .eq('id', messageId)
        .single();

      if (fetchError) throw fetchError;

      // Create conversation and send message
      const { conversationId } = await this.getOrCreateConversation(pending.sender_id);

      // Mark conversation as approved
      await supabase
        .from('conversations')
        .update({
          first_message_approved: true,
          requires_approval: false,
        })
        .eq('id', conversationId);

      // Send the message
      await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: pending.sender_id,
        content: pending.content,
        created_at: pending.created_at,
      });

      // Mark pending message as approved
      await supabase
        .from('pending_messages')
        .update({ approved_at: new Date().toISOString() })
        .eq('id', messageId);

      return { success: true, conversationId };
    } catch (error) {
      console.error('Error approving message:', error);
      return { success: false, error: error.message };
    }
  }

  async rejectPendingMessage(messageId, reason = '') {
    try {
      const { error } = await supabase
        .from('pending_messages')
        .update({
          rejected_at: new Date().toISOString(),
          rejection_reason: reason,
        })
        .eq('id', messageId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error rejecting message:', error);
      return { success: false, error: error.message };
    }
  }

  // ============================================
  // Host Features
  // ============================================

  async broadcastToAttendees(eventId, content, attachments = []) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get all event attendees
      const { data: attendees, error: attendeeError } = await supabase
        .from('rsvps')
        .select('user_id')
        .eq('event_id', eventId)
        .eq('status', 'going');

      if (attendeeError) throw attendeeError;

      // Create conversations and send messages
      const results = [];
      for (const attendee of attendees) {
        if (attendee.user_id !== user.id) {
          const { conversationId } = await this.getOrCreateConversation(attendee.user_id, eventId);

          // Mark as not requiring approval for host broadcasts
          await supabase
            .from('conversations')
            .update({
              requires_approval: false,
              first_message_approved: true,
            })
            .eq('id', conversationId);

          const result = await this.sendMessage(conversationId, content, attachments);
          results.push(result);
        }
      }

      return {
        success: true,
        sent: results.filter((r) => r.success).length,
        failed: results.filter((r) => !r.success).length,
      };
    } catch (error) {
      console.error('Error broadcasting to attendees:', error);
      return { success: false, error: error.message };
    }
  }

  async getMessageStats() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get basic stats
      const stats = {
        totalConversations: 0,
        unreadMessages: 0,
        avgResponseTime: null,
        totalMessages: 0,
      };

      // Get conversation stats
      const { data: convStats } = await supabase
        .from('conversation_participants')
        .select('unread_count')
        .eq('user_id', user.id);

      if (convStats) {
        stats.totalConversations = convStats.length;
        stats.unreadMessages = convStats.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      }

      return { success: true, data: stats };
    } catch (error) {
      console.error('Error fetching message stats:', error);
      return { success: false, error: error.message };
    }
  }

  // ============================================
  // Real-time Subscriptions
  // ============================================

  subscribeToMessages(conversationId, callback) {
    return supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        callback
      )
      .subscribe();
  }

  subscribeToTyping(conversationId, callback) {
    return supabase
      .channel(`typing:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'typing_indicators',
          filter: `conversation_id=eq.${conversationId}`,
        },
        callback
      )
      .subscribe();
  }

  async sendTypingIndicator(conversationId) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      await supabase.from('typing_indicators').upsert({
        conversation_id: conversationId,
        user_id: user.id,
        started_at: new Date().toISOString(),
      });

      return { success: true };
    } catch (error) {
      console.error('Error sending typing indicator:', error);
      return { success: false, error: error.message };
    }
  }

  async clearTypingIndicator(conversationId) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      await supabase
        .from('typing_indicators')
        .delete()
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id);

      return { success: true };
    } catch (error) {
      console.error('Error clearing typing indicator:', error);
      return { success: false, error: error.message };
    }
  }
}

// Create singleton instance
const messagingService = new MessagingService();

export default messagingService;
